import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useUser } from "@/hooks/useUserProvider";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { request } from "@/lib/request";
import { toast } from "sonner";
import { useGetTiers } from "@/hooks/queries/useGetTiers";
import { ChevronsUpIcon, Loader2Icon, XIcon } from "lucide-react";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils";

export function SubscriptionsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
}) {
  const { user, setUserModalOpen } = useUser();
  const [isRedirecting, setIsRedirecting] = useState<string | null>(null);

  const [selectedInterval, setSelectedInterval] = useState<
    "monthly" | "yearly"
  >("monthly");

  const {
    data: tiers,
    isLoading,
    isError,
    error,
  } = useGetTiers({
    enabled: open,
  });

  const tier = tiers?.find(tier => tier.slug === "plus");
  const supportsInterval = tier?.intervals.includes(selectedInterval);
  const price = supportsInterval
    ? tier?.prices.find(p => p.type === selectedInterval)?.price
    : tier?.basePrice;

  const handlePurchase = async (tierId: string, interval: string) => {
    try {
      setIsRedirecting(tierId);

      const res = await request
        .post(`/payment/subscriptions`, { tierId, interval })
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : `Failed to create subscription checkout session: ${err.response?.data}`,
          );
        });

      const session = await res.data;

      if (session?.url) {
        window.location.href = session.url;
      } else {
        throw new Error("No checkout URL returned from server.");
      }
    } catch (err: any) {
      onOpenChange(false);
      setUserModalOpen(true);
      setIsRedirecting(null);
      toast.error(
        err?.message === "Unauthorized"
          ? "Please login to your account"
          : err?.message || "Failed to create subscription checkout session.",
      );
    }
  };

  const trialAvailable = useMemo(() => {
    if (import.meta.env.VITE_UNIDECK_PLUS_TRIAL === "true") {
      return true;
    }
    if (!user?.subscription || user?.subscription?.tier.slug !== "plus") {
      return true;
    }
    return false;
  }, [user]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        disableClose
        className="max-h-[calc(100vh-200px)] w-full max-w-[600px] overflow-y-auto border-0 px-4 focus-visible:ring-0 sm:px-6 md:px-8"
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 text-left">
          <DialogTitle>Subscription Plans</DialogTitle>
          <DialogClose asChild>
            <Button
              variant="secondary"
              className="flex-shrink-0 rounded-xl"
              size="icon"
            >
              <XIcon className="size-4" />
              <span className="sr-only">Close</span>
            </Button>
          </DialogClose>
        </DialogHeader>
        {isLoading ? (
          <div className="flex flex-col items-center gap-4">
            <Skeleton className="h-[24px] w-[80px] rounded" />
            <Skeleton className="h-[64px] w-[100px] rounded" />
            <div className="grid w-full grid-cols-2 gap-4">
              <Skeleton className="h-[110px] w-full rounded" />
              <Skeleton className="h-[110px] w-full rounded" />
              <Skeleton className="h-[110px] w-full rounded" />
              <Skeleton className="h-[110px] w-full rounded" />
            </div>
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to fetch tiers</AlertTitle>
            <AlertDescription>
              {(error as Error)?.message ?? "Something went wrong."}
            </AlertDescription>
          </Alert>
        ) : !tier ? (
          <Alert variant="destructive">
            <AlertTitle>No tiers found</AlertTitle>
            <AlertDescription>
              We couldn't find any subscription tiers at this time.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2">
              <span>Monthly</span>
              <Switch
                checked={selectedInterval === "yearly"}
                onCheckedChange={value =>
                  setSelectedInterval(value ? "yearly" : "monthly")
                }
              />
              <span>Yearly</span>
            </div>
            <div className="flex w-full flex-col items-center gap-4">
              <div className="mb-4 flex flex-col items-center gap-1">
                <div className="flex items-center gap-4">
                  <span
                    className={cn(
                      "text-4xl font-bold",
                      trialAvailable && "line-through",
                    )}
                  >
                    {tier.currency.toUpperCase() === "EUR"
                      ? "€"
                      : tier.currency}{" "}
                    {price}
                  </span>
                  {selectedInterval === "yearly" ? (
                    <Badge
                      variant="default"
                      className="bg-green-500 text-sm text-white"
                    >
                      Save 20%
                    </Badge>
                  ) : null}
                </div>
                <span
                  className={cn(
                    "text-sm text-muted-foreground",
                    trialAvailable && "line-through",
                  )}
                >
                  per month
                </span>
              </div>
              {trialAvailable ? (
                <span className="-mt-4 mb-4 text-sm">
                  Get 1 month free trial, no credit card required.
                </span>
              ) : null}
              <div className="grid w-full grid-cols-2 gap-4">
                <div className="flex w-full flex-col gap-2 rounded-xl border border-border p-4">
                  <span className="text-lg font-bold">Premium widgets</span>
                  <span className="text-sm">
                    Access to our growing library of premium widgets.
                  </span>
                </div>
                <div className="flex w-full flex-col gap-2 rounded-xl border border-border p-4">
                  <span className="text-lg font-bold">AI Insights</span>
                  <span className="text-sm">
                    Get insights on your calendar, emails, todos and more using
                    AI.
                  </span>
                </div>
                <div className="flex w-full flex-col gap-2 rounded-xl border border-border p-4">
                  <span className="text-lg font-bold">More integrations</span>
                  <span className="text-sm">
                    Connect your data from our growing list of supported
                    services.
                  </span>
                </div>
                <div className="flex w-full flex-col gap-2 rounded-xl border border-border p-4">
                  <span className="text-lg font-bold">Priority support</span>
                  <span className="text-sm">
                    Get priority support from our team.
                  </span>
                </div>
              </div>
              <div className="flex w-full items-center justify-center">
                <Button
                  variant="default"
                  disabled={!!isRedirecting}
                  onClick={() => {
                    handlePurchase(tier.id, selectedInterval);
                  }}
                >
                  {isRedirecting ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : (
                    <ChevronsUpIcon className="size-4" />
                  )}
                  Upgrade to {tier.name}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
